import styled, {keyframes} from "styled-components";
import {useMessages} from "i18n";
import {fontFamilies, fontWeights, zIndex} from "../../theme/tokens";

/**
 * Full-viewport affordance shown while the user drags files over the page — the design's
 * "2 Drag" screen (`.overlay` / `.ov-frame`): a blue→teal gradient wash with a pulsing dashed
 * frame, the Instrument-Serif "Drop it anywhere." display and a mono release hint.
 */

/** Dark-mode overlay gradient stops, verbatim from the design (`.app.dark .overlay`). */
const DARK_OVERLAY = "linear-gradient(135deg, #172a66, #0b5e50)";

const pulse = keyframes`
	0%, 100% { transform: scale(1); }
	50% { transform: scale(1.012); }
`;

const Scrim = styled.div`
	position: fixed;
	inset: 0;
	/* Above an open dialog on purpose — a drop target must win over any modal (tokens.zIndex). */
	z-index: ${zIndex.dropOverlay};
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 22px;
	color: #fff;
	background: ${({theme}) =>
		theme.tokens.mode === "dark"
			? DARK_OVERLAY
			: `linear-gradient(135deg, ${theme.tokens.blue}, ${theme.tokens.teal})`};
	backdrop-filter: blur(8px);
`;

const Frame = styled.div`
	border: 2.5px dashed rgba(255, 255, 255, 0.55);
	border-radius: 32px;
	padding: 8vh 7vw;
	text-align: center;
	animation: ${pulse} 1.8s ease-in-out infinite;
`;

const Heading = styled.div`
	font-family: ${fontFamilies.displaySerif};
	font-weight: ${fontWeights.display};
	/*
	 * Restored with the display face (ui#1257). The constraint here is the ACCENT WORD rather than the
	 * line: it is a single unbreakable token inside a fixed-position scrim with no overflow guard, so
	 * it does not wrap, it runs out of the frame. The frame is 8vh/7vw padding plus its dashed border,
	 * so the interior is 0.86 * viewport - 5 and the fit is arithmetic. ui#1219 had to cut this to
	 * clamp(35px, 5vw, 80px) because in a face that set ~1.5x wider "anywhere." measured 273px inside
	 * a 270.2px interior at 320px and ran out of the frame. Re-measured in this face at the 52px floor
	 * a 320px viewport lands on: "anywhere." is 177.17px and "oriunde." 144.47px, so the tightest word
	 * clears that same interior by 93px — and the single-line composition returns from 375px up.
	 */
	font-size: clamp(52px, 7.5vw, 120px);
	line-height: 1;
	letter-spacing: -0.015em;
`;

const Accent = styled.span`
	font-style: italic;
	opacity: 0.85;
`;

const ReleaseHint = styled.div`
	font-family: ${fontFamilies.mono};
	font-size: 13px;
	letter-spacing: 0.12em;
	text-transform: uppercase;
	color: rgba(255, 255, 255, 0.75);
	margin-top: 26px;
`;

export function DropOverlay() {
	const {sender} = useMessages();
	return (
		<Scrim>
			<Frame>
				<Heading>{sender.overlay.title} <Accent>{sender.overlay.titleAccent}</Accent></Heading>
				<ReleaseHint>{sender.overlay.release}</ReleaseHint>
			</Frame>
		</Scrim>
	);
}
